import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';

import { searchOpsAccounts } from '../../../requests';
import { inputClass } from '../ui';

export type TPickedAccount = { id: string; name: string };

/**
 * Type-ahead over /ops/classes/accounts. Shows the chosen account as a
 * chip with a "change" link, the same shape the class editor uses.
 */
const AccountPicker: React.FC<{
  value: TPickedAccount | null;
  onChange: (a: TPickedAccount | null) => void;
  placeholder?: string;
  className?: string;
}> = ({ value, onChange, placeholder, className }) => {
  const [query, setQuery] = useState('');
  const { data: hits = [] } = useQuery({
    queryKey: ['opsAccounts', query],
    queryFn: () => searchOpsAccounts(query),
    enabled: !value && query.trim().length >= 2,
  });

  if (value) {
    return (
      <div className={`flex items-center gap-2 text-sm ${className ?? ''}`}>
        <span className="rounded-md bg-blue-100 px-2.5 py-1 font-medium text-blue-primary">
          {value.name}
        </span>
        <button
          type="button"
          className="text-xs font-medium text-gray-bold underline hover:text-blue-primary"
          onClick={() => {
            onChange(null);
            setQuery('');
          }}
        >
          change
        </button>
      </div>
    );
  }

  return (
    <div className={`relative ${className ?? ''}`}>
      <input
        className={`${inputClass} w-full`}
        placeholder={placeholder ?? 'Find an account…'}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {hits.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-md border border-white-regular bg-white shadow-light">
          {hits.map((a) => (
            <button
              key={a.id}
              type="button"
              className="block w-full px-3 py-2 text-left text-sm hover:bg-blue-100/40"
              onClick={() => {
                onChange({ id: a.id, name: a.name });
                setQuery('');
              }}
            >
              <span className="font-medium text-gray-four">{a.name}</span>
              {a.city && (
                <span className="ml-2 text-xs text-gray-two">{a.city}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AccountPicker;
